import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AddProspectAccountInline } from './AddProspectAccountInline';
import {
  Search, Building2, Loader2, X, Phone, UserPlus, ArrowLeft, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { EIGHT_X_EIGHT_LIME, EIGHT_X_EIGHT_DARK, splitEightX8Name } from './EightXEightCallsBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Add Account / Contact to CRM" — opened from an unmatched 8x8 call row
 * (no account found for the number that called). Search CRM for an
 * existing account first; if found, either attach this phone number to it
 * directly, or add a brand-new contact (with this number) to it. If no
 * account exists at all, fall through to creating a brand-new one (reusing
 * the same prospect-account flow used elsewhere in the app).
 *
 * On success, calls `onLinked({ account, contact })` — the caller persists
 * this onto the 8x8 call row via PATCH /8x8/active-calls/{id}/link.
 */
export const EightXEightLinkAccountModal = ({ call, onClose, onLinked }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);   // {id, name} once picked from results
  const [action, setAction] = useState(null);                     // 'phone' | 'contact' once an existing account is picked
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // Contact-add mini form (used when action === 'contact') — pre-filled from
  // whatever name 8x8 itself already has saved against this number (a
  // rep's personal 8x8 contact, or an org-wide 8x8 address-book entry), so
  // the rep isn't retyping a name we already have.
  const _prefill = splitEightX8Name(call?.eight_x8_name);
  const [firstName, setFirstName] = useState(_prefill.first);
  const [lastName, setLastName] = useState(_prefill.last);
  const [email, setEmail] = useState('');
  const [contactPhone, setContactPhone] = useState(call?.phone_number || '');
  // Phone-only form (used when action === 'phone')
  const [accountPhone, setAccountPhone] = useState(call?.phone_number || '');

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`${API}/zoho/search-accounts`, { params: { q: query.trim() } });
        setResults(res.data?.accounts || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleAddPhone = async () => {
    if (!accountPhone.trim()) return toast.error('Enter a phone number');
    setSaving(true);
    try {
      await api.patch(`${API}/zoho/accounts/${selectedAccount.id}/phone`, { phone: accountPhone.trim() });
      toast.success('Number added to account');
      onLinked({ account: selectedAccount, contact: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update account');
    } finally { setSaving(false); }
  };

  const handleAddContact = async () => {
    const ln = lastName.trim();
    if (!ln) return toast.error('Contact last name is required');
    setSaving(true);
    try {
      const res = await api.post(`${API}/zoho/create-contact`, {
        account_id: selectedAccount.id,
        first_name: firstName.trim() || null,
        last_name: ln,
        email: email.trim() || null,
        phone: contactPhone.trim() || null,
      });
      toast.success('Contact added to account');
      onLinked({ account: selectedAccount, contact: res.data.contact ? { id: res.data.contact.id, name: res.data.contact.full_name } : null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create contact');
    } finally { setSaving(false); }
  };

  const reset = () => { setSelectedAccount(null); setAction(null); setShowCreateNew(false); };

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3" data-testid="8x8-link-account-modal">
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ border: `3px solid ${EIGHT_X_EIGHT_LIME}` }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: EIGHT_X_EIGHT_LIME }}>
              <UserPlus className="w-4 h-4" style={{ color: EIGHT_X_EIGHT_DARK }} />
            </span>
            <div>
              <h3 className="text-sm font-extrabold text-gray-900">Add Account / Contact to CRM</h3>
              <p className="text-[11px] text-gray-500">{call?.phone_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400" data-testid="8x8-link-account-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {showCreateNew ? (
            <>
              <button onClick={() => setShowCreateNew(false)} className="text-xs font-semibold text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2" data-testid="8x8-link-back-to-search">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to search
              </button>
              <AddProspectAccountInline
                themeColor="purple"
                initialPhone={call?.phone_number || ''}
                onCancel={() => setShowCreateNew(false)}
                onCreated={({ account, contact }) => {
                  toast.success('Account created');
                  onLinked({
                    account,
                    contact: contact?.id ? { id: contact.id, name: contact.full_name } : null,
                  });
                }}
              />
            </>
          ) : !selectedAccount ? (
            <>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search CRM for an existing account…"
                  className="pl-9 h-11 rounded-xl border-2"
                  data-testid="8x8-link-search-input"
                />
                {searching && <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
              </div>

              <div className="space-y-1.5 max-h-64 overflow-y-auto mb-3">
                {results.map(a => (
                  <button
                    key={a.id}
                    onClick={() => setSelectedAccount({ id: a.id, name: a.name })}
                    className="w-full flex items-center gap-2 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 px-3 py-2 text-left transition-colors"
                    data-testid={`8x8-link-account-result-${a.id}`}
                  >
                    <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{a.name}</div>
                      {a.city && <div className="text-[11px] text-gray-400">{a.city}</div>}
                    </div>
                  </button>
                ))}
                {query.trim() && !searching && results.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-3">No matching accounts found.</p>
                )}
              </div>

              <Button
                variant="outline"
                onClick={() => setShowCreateNew(true)}
                className="w-full rounded-xl h-10 text-sm font-semibold border-2"
                data-testid="8x8-link-create-new-btn"
              >
                <Building2 className="w-4 h-4 mr-2" /> Can't find it? Create a new account
              </Button>
            </>
          ) : !action ? (
            <>
              <button onClick={reset} className="text-xs font-semibold text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-3" data-testid="8x8-link-back-to-search-2">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to search
              </button>
              <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-bold text-gray-800">{selectedAccount.name}</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">This account is in CRM, but this number isn't on file yet. What do you want to do?</p>
              <div className="space-y-2">
                <button
                  onClick={() => setAction('phone')}
                  className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 hover:border-[#CBDD2C] hover:bg-[#f7f9e3] px-4 py-3 text-left transition-colors"
                  data-testid="8x8-link-action-phone"
                >
                  <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-800">Add this number to the account</span>
                </button>
                <button
                  onClick={() => setAction('contact')}
                  className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 hover:border-[#CBDD2C] hover:bg-[#f7f9e3] px-4 py-3 text-left transition-colors"
                  data-testid="8x8-link-action-contact"
                >
                  <UserPlus className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-800">Add a new contact to the account</span>
                </button>
              </div>
            </>
          ) : action === 'phone' ? (
            <>
              <button onClick={() => setAction(null)} className="text-xs font-semibold text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Phone number</label>
              <Input value={accountPhone} onChange={(e) => setAccountPhone(e.target.value)} className="h-11 rounded-xl border-2 mt-1 mb-4" data-testid="8x8-link-phone-input" />
              <Button onClick={handleAddPhone} disabled={saving} className="w-full rounded-xl h-10 text-white font-semibold" style={{ backgroundColor: EIGHT_X_EIGHT_DARK }} data-testid="8x8-link-phone-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Add number to {selectedAccount.name}</>}
              </Button>
            </>
          ) : (
            <>
              <button onClick={() => setAction(null)} className="text-xs font-semibold text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className="h-11 rounded-xl border-2" data-testid="8x8-link-contact-first" />
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name *" className="h-11 rounded-xl border-2" data-testid="8x8-link-contact-last" />
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" type="email" className="h-11 rounded-xl border-2 col-span-2" data-testid="8x8-link-contact-email" />
                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Phone" className="h-11 rounded-xl border-2 col-span-2" data-testid="8x8-link-contact-phone" />
              </div>
              <Button onClick={handleAddContact} disabled={saving} className="w-full rounded-xl h-10 text-white font-semibold" style={{ backgroundColor: EIGHT_X_EIGHT_DARK }} data-testid="8x8-link-contact-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Add contact to {selectedAccount.name}</>}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EightXEightLinkAccountModal;
